/**
 * Application shell: owns the engine, input, HUD and session, and swaps scenes.
 *
 * Routing is hash-based (`#/hub`, `#/game1`, `#/game2`) rather than history-based
 * so the build is a pure static bundle with no server rewrite rules, and so the
 * capture harness can deep-link straight to a scene for screenshots.
 */
import { startGameMusic, stopGameMusic } from '../audio';
import { academyCelebrationPending } from './academyProgress';
import { Engine } from './engine';
import { Input } from './input';
import { GameSession } from './session';
import { Hud } from '../ui/hud';
import { installCssVars } from '../render/palette';
import { disposeForgeTextures } from '../render/textures';
import { OrientationGate } from '../ui/orientationGate';
import type { GameScene, RouteId, SceneContext } from './types';

export type { RouteId } from './types';

const ROUTES: readonly RouteId[] = ['welcome', 'hub', 'game1', 'game2', 'game3', 'academy-ending'];

export type SceneFactory = () => Promise<GameScene> | GameScene;

export interface SceneLifecycle {
  entered(route: RouteId): void;
  exiting(route: RouteId): void;
}

export class App {
  readonly engine: Engine;
  readonly input: Input;
  readonly session = new GameSession();
  readonly hud: Hud;
  private readonly orientationGate: OrientationGate;

  private current?: GameScene;
  private currentId?: RouteId;
  /** Guards against overlapping navigations (double-click, rapid hash edits). */
  private transitionToken = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    hudRoot: HTMLElement,
    private readonly factories: Record<RouteId, SceneFactory>,
    private readonly lifecycle?: SceneLifecycle
  ) {
    installCssVars();
    this.orientationGate = new OrientationGate(canvas.closest<HTMLElement>('#stage')!);

    this.engine = new Engine({ canvas, viewHeight: 12, tiltDeg: 42, yawDeg: 0 });
    this.engine.observeResize();

    this.input = new Input();
    this.hud = new Hud({
      root: hudRoot,
      input: this.input,
      onPause: () => this.togglePause(),
      onExit: () => this.goto('hub'),
    });
    this.hud.bind((fn) => this.session.subscribe(fn));

    // One input resolve per frame, before any scene reads it.
    this.engine.onUpdate((dt, elapsed) => {
      const state = this.input.update();
      // Keep timers and simulation still while the device-rotation prompt owns input.
      if (this.orientationGate.blocked) {
        this.input.releaseAll();
        return;
      }
      if (state.pausePressed) this.togglePause();
      this.session.tick(dt);
      this.current?.update(dt, elapsed);
    });

    window.addEventListener('hashchange', () => {
      const id = this.routeFromHash();
      if (id !== this.currentId) void this.goto(id);
    });
  }

  private routeFromHash(): RouteId {
    const raw = window.location.hash.replace(/^#\/?/, '').split(/[?/]/)[0] ?? '';
    return (ROUTES as readonly string[]).includes(raw) ? (raw as RouteId) : 'welcome';
  }

  async start(): Promise<void> {
    this.engine.start();
    await this.goto(this.routeFromHash());
  }

  async goto(route: RouteId): Promise<void> {
    // Keep the game's results visible until the player leaves them. Whichever
    // game finishes last, intercept the next navigation before hub or replay.
    if (this.currentId !== 'academy-ending' && academyCelebrationPending()) {
      route = 'academy-ending';
    }
    const token = ++this.transitionToken;
    stopGameMusic();

    // Tear the old scene down BEFORE building the new one, so GPU resources
    // never double-peak during a transition on low-memory mobile devices.
    if (this.current) {
      if (this.currentId) this.lifecycle?.exiting(this.currentId);
      try {
        this.current.exit();
      } catch (err) {
        console.error('[app] scene exit failed', err);
      }
      this.current = undefined;
    }

    this.currentId = route;
    this.orientationGate.setRoute(route);
    if (this.routeFromHash() !== route) {
      window.location.hash = `#/${route}`;
    }

    // One HUD instance serves every scene, so hand the incoming scene a clean
    // one rather than the outgoing scene's leftovers. Scenes opt back in.
    this.hud.resetForScene();

    const ctx: SceneContext = {
      engine: this.engine,
      input: this.input,
      hud: this.hud,
      session: this.session,
      goto: (r) => void this.goto(r),
    };

    let scene: GameScene;
    try {
      scene = await this.factories[route]();
      // A newer navigation started while we were loading — abandon this one.
      if (token !== this.transitionToken) {
        scene.exit?.();
        return;
      }
      await scene.enter(ctx);
      if (token !== this.transitionToken) {
        scene.exit?.();
        return;
      }
    } catch (err) {
      console.error(`[app] failed to enter scene "${route}"`, err);
      return;
    }

    this.current = scene;
    if (route === 'game1' || route === 'game2' || route === 'game3') startGameMusic(route);
    document.documentElement.setAttribute('data-route', route);
    this.lifecycle?.entered(route);
  }

  private togglePause(): void {
    if (this.session.phase === 'playing') {
      this.session.pause();
      this.input.setEnabled(false);
    } else if (this.session.phase === 'paused') {
      this.session.resume();
      this.input.setEnabled(true);
    }
  }

  dispose(): void {
    stopGameMusic();
    if (this.currentId) this.lifecycle?.exiting(this.currentId);
    this.current?.exit();
    this.hud.dispose();
    this.orientationGate.dispose();
    this.input.dispose();
    this.engine.dispose();
    // The forge texture cache deliberately outlives scene swaps — those are
    // shared world assets and `disposeTree` leaves them alone by design. Whole-app
    // teardown is the one moment it is correct to release them.
    disposeForgeTextures();
    void this.canvas;
  }
}
